import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { FaSpinner, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { tranzilaService, TranzilaPaymentRequest } from "../services/tranzila";
import {
  registrationService,
  workshopService,
  Workshop,
} from "../services/firebase";
import { PAYMENT_URLS } from "../config/tranzila";
import "./Payment.css";
import { useTranslation } from "react-i18next";
import { formatDateIsrael } from "../utils/dateUtils";

const Payment: React.FC = () => {
  const { registrationId } = useParams<{ registrationId: string }>();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const [workshop, setWorkshop] = useState<Workshop | null>(null);
  const currentLanguage = i18n.language;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<
    "pending" | "success" | "failed" | "cancelled"
  >("pending");
  const [registration, setRegistration] = useState<any>(null);
  const [iframeUrl, setIframeUrl] = useState<string>("");

  // Get localized content based on current language
  const getLocalizedTitle = () => {
    if (!workshop) return "";
    if (currentLanguage === "ru" && workshop.titleRu) {
      return workshop.titleRu;
    }
    return workshop.title;
  };

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const status = searchParams.get("status");
      const transactionId = searchParams.get("transaction_id");
      const errorCode = searchParams.get("error_code");
      const tranzilaError = searchParams.get("TranzilaError");
      const errorMessage = searchParams.get("error_message");

      // בדיקה אם התשלום באמת הצליח
      const isPaymentSuccess =
        status === "success" && !errorCode && !tranzilaError && !errorMessage;

      
      if (transactionId) {
        if (isPaymentSuccess) {
          if (registrationId) {
            try {
              await registrationService.updatePaymentStatus(
                registrationId,
                "paid",
                transactionId
              );

              // עדכון סטטוס ההרשמה לאושר אחרי תשלום מוצלח
              await registrationService.updateStatus(
                registrationId,
                "confirmed"
              );

              // עדכון מספר המקומות הפנויים אחרי תשלום מוצלח (פעם אחת בלבד)
              try {
                await registrationService.confirmRegistrationSpots(
                  registrationId
                );
                              } catch (error) {
                              }

              // רק אחרי שכל הבדיקות עוברות - הגדרת סטטוס הצלחה
              setPaymentStatus("success");
              setSuccess(true);
            } catch (error) {
                          }
          }
        } else {
          // תשלום נכשל - paymentCallback כבר יפנה לדף כישלון
                  }
      }
    };

    const loadWorkshop = async () => {
      if (!registrationId) {
        setError("מזהה הרשמה לא תקין");
        setLoading(false);
        return;
      }

      try {
        // טעינת פרטי ההרשמה
        const registrationData = await registrationService.getById(
          registrationId
        );
        setRegistration(registrationData);

        // טעינת פרטי הסדנה
        const workshopData = await workshopService.getById(
          registrationData.workshopId
        );
        setWorkshop(workshopData);

        // פרטי הלקוח כבר נטענו

        // מעבר ישר לתשלום בלי להציג טופס
        if (registrationData && workshopData) {
          // יצירת פרטי התשלום
          const paymentData: TranzilaPaymentRequest = {
            sum: registrationData.totalAmount || workshopData.price,
            currency: "ILS",
            description: `הרשמה לסדנה: ${workshopData.title}${
              registrationData.ticketsCount && registrationData.ticketsCount > 1
                ? ` (${registrationData.ticketsCount} כרטיסים)`
                : ""
            }`,
            orderId: tranzilaService.generateOrderId(),
            customerName: registrationData.name,
            customerEmail: registrationData.email,
            customerPhone: registrationData.phone,
            returnUrl: PAYMENT_URLS.success(registrationId),
            cancelUrl: PAYMENT_URLS.cancelled(registrationId),
            notifyUrl: PAYMENT_URLS.notify(registrationId),
          };

          // בדיקת תקינות
          const validation = tranzilaService.validatePaymentData(paymentData);
          if (validation.valid) {
            // יצירת התשלום עם השפה הנוכחית
            tranzilaService
              .createPayment(paymentData, i18n.language)
              .then((response) => {
                if (response.success && response.iframeUrl) {
                  // עדכון פרטי ההרשמה
                  registrationService.updatePaymentStatus(
                    registrationId,
                    "pending",
                    response.transactionId,
                    paymentData.orderId,
                    registrationData.totalAmount || workshopData.price
                  );

                  // הצגת ה-iframe
                  setIframeUrl(response.iframeUrl);
                  setLoading(false);
                } else {
                  setError(response.error || "שגיאה ביצירת התשלום");
                  setLoading(false);
                }
              })
              .catch((error) => {
                                setError("שגיאה בתהליך התשלום");
                setLoading(false);
              });
          } else {
            setError(validation.errors.join(", "));
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      } catch (error) {
                setError("שגיאה בטעינת פרטי הסדנה");
        setLoading(false);
      }
    };

    checkPaymentStatus();
    loadWorkshop();
  }, [registrationId, searchParams, i18n.language]);

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setError("שגיאה בטעינת טופס התשלום");
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-spinner">
          <FaSpinner className="fa-spin" />
          {t("loading")}{" "}
        </div>
      </div>
    );
  }

  if (paymentStatus === "success" && success) {
    return (
      <div className="page-container">
        <div className="status-container">
          <div className="status-icon">
            <FaCheckCircle style={{ color: "#4CAF50" }} />
          </div>
          <h1 className="payment-title">התשלום הושלם בהצלחה!</h1>
          <p className="payment-subtitle">
            תודה על הרשמתך לסדנה. תקבל אישור במייל בקרוב.
          </p>
        </div>
      </div>
    );
  }

  if (paymentStatus === "failed" || paymentStatus === "cancelled") {
    return (
      <div className="page-container">
        <div className="status-container">
          <div className="status-icon">
            <FaTimesCircle style={{ color: "#f44336" }} />
          </div>
          <h1 className="payment-title">
            {paymentStatus === "failed" ? "התשלום נכשל" : "התשלום בוטל"}
          </h1>
          <p className="payment-subtitle">
            {paymentStatus === "failed"
              ? "אירעה שגיאה בתהליך התשלום. אנא נסה שוב."
              : "התשלום בוטל על ידך."}
          </p>
        </div>
      </div>
    );
  }

  if (iframeUrl) {
    const { width, height } = tranzilaService.getIframeDimensions();
    const styles = tranzilaService.getIframeStyles();

    return (
      <div className="page-container">
        <div className="payment-card">
          <div className="payment-header">
            <h1 className="payment-title">
              {t("paymentForWorkshopRegistration")}
            </h1>
            <p className="payment-subtitle">
              {t("pleaseCompletePaymentDetails")}
            </p>
          </div>

          {workshop && (
            <div className="workshop-info">
              <h3 className="workshop-title">{getLocalizedTitle()}</h3>
              <div className="workshop-details">
                {registration?.workshopDate && (
                  <span>
                    {t("date")}:{" "}
                    {formatDateIsrael(registration.workshopDate)}
                  </span>
                )}
                {registration?.workshopTime && (
                  <span>
                    {t("time")}: {registration.workshopTime}
                  </span>
                )}
                {registration?.ticketsCount &&
                  registration.ticketsCount > 1 && (
                    <span>מספר כרטיסים: {registration.ticketsCount}</span>
                  )}
                <div className="price">
                  {registration?.totalAmount || workshop.price} ₪
                </div>
              </div>
            </div>
          )}

          <div className="iframe-container">
            <iframe
              src={iframeUrl}
              width={width}
              height={height}
              style={styles}
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              title="Tranzila Payment Form"
              sandbox="allow-forms allow-scripts allow-same-origin allow-top-navigation"
              allow="payment"
            />
          </div>

          <div className="iframe-footer">
            <p className="security-note"> {t("securePaymentTranzila")}</p>
          </div>
        </div>
      </div>
    );
  }

  // אם יש iframe URL, הצג אותו
  if (iframeUrl) {
    return (
      <div className="page-container">
        <div className="payment-card">
          <div className="payment-header">
            <h1 className="payment-title">
              {t("paymentForWorkshopRegistration")}
            </h1>
            <p className="payment-subtitle">
              {t("pleaseCompletePaymentDetails")}
            </p>
          </div>

          {workshop && (
            <div className="workshop-info">
              <h3 className="workshop-title">{getLocalizedTitle()}</h3>
              <div className="workshop-details">
                {registration?.workshopDate && (
                  <span>
                    תאריך:{" "}
                    {formatDateIsrael(registration.workshopDate)}
                  </span>
                )}
                {registration?.workshopTime && (
                  <span>שעה: {registration.workshopTime}</span>
                )}
                {registration?.ticketsCount &&
                  registration.ticketsCount > 1 && (
                    <span>מספר כרטיסים: {registration.ticketsCount}</span>
                  )}
                <div className="price">
                  ₪{registration?.totalAmount || workshop.price}
                </div>
              </div>
            </div>
          )}

          <div className="iframe-container">
            <iframe
              src={iframeUrl}
              width="100%"
              height="600"
              frameBorder="0"
              title="Tranzila Payment"
              style={{
                border: "none",
                borderRadius: "8px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // אם יש שגיאה, הצג אותה
  if (error) {
    return (
      <div className="page-container">
        <div className="payment-card">
          <div className="payment-header">
            <h1 className="payment-title">שגיאה בתשלום</h1>
            <p className="payment-subtitle">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="loading-spinner">
        <FaSpinner className="fa-spin" />
        {t("loading")}
      </div>
    </div>
  );
};

export default Payment;
